import React from "react";
import { X, ArrowRight, MessageCircle } from "lucide-react";
import { BOOK_CALL_URL, WHATSAPP_URL } from "@/lib/constants";

const dontNeed = ["Technical knowledge", "New software", "Extra team"];

const decisionSupport = [
  { tag: "Initiate", hint: "if you’re missing leads" },
  { tag: "Guide", hint: "if conversations aren’t converting" },
  { tag: "Core", hint: "if you want full revenue impact" },
  { tag: "Pulse", hint: "when you want continuous growth" },
];

export default function CloseBlock() {
  return (
    <>
      {/* Risk Reversal — cream */}
      <section
        data-testid="risk-section"
        className="relative py-20 lg:py-28"
        style={{ background: "var(--cream)" }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
            <div>
              <h2
                className="font-display text-4xl sm:text-5xl lg:text-[58px] leading-[1.1]"
                data-testid="risk-headline"
              >
                You don’t need to{" "}
                <span className="font-display-italic">lift a finger.</span>
              </h2>
              <p
                className="mt-6 text-lg"
                style={{ color: "var(--text-muted)" }}
              >
                We handle the setup. You get the system. That’s the whole deal.
              </p>

              <div className="mt-8 space-y-3">
                {dontNeed.map((d, i) => (
                  <div
                    key={d}
                    className="flex items-center gap-3 rounded-2xl px-5 py-4 card-hover"
                    style={{
                      background: "var(--cream-soft)",
                      border: "1px solid var(--line)",
                    }}
                    data-testid={`risk-item-${i}`}
                  >
                    <span
                      className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0"
                      style={{ background: "rgba(201,75,75,0.1)" }}
                    >
                      <X size={16} className="text-[#c94b4b]" strokeWidth={3} />
                    </span>
                    <span className="text-[15px] text-[var(--ink)] font-medium">
                      No {d.toLowerCase()} needed
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="rounded-[32px] p-8 lg:p-10 text-white relative overflow-hidden card-hover-dark"
              style={{ background: "var(--ink)" }}
            >
              <div className="grain absolute inset-0" />
              <div className="relative">
                <h3 className="font-display text-3xl lg:text-4xl leading-tight">
                  A fully-implemented{" "}
                  <span className="font-display-italic">Revenue System</span>{" "}
                  inside your WhatsApp.
                </h3>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  {[
                    { k: "Setup", v: "Done for you" },
                    { k: "Integrations", v: "Website · CRM · Payments" },
                    { k: "Flows", v: "Capture → Convert → Repeat" },
                    { k: "Support", v: "Strategy + Optimisation" },
                  ].map((x) => (
                    <div
                      key={x.k}
                      className="rounded-2xl p-4"
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <div
                        className="text-[11px] uppercase tracking-widest"
                        style={{ color: "var(--gold-light)" }}
                      >
                        {x.k}
                      </div>
                      <div
                        className="mt-1.5 text-sm font-semibold"
                        style={{ color: "rgba(248,241,231,0.92)" }}
                      >
                        {x.v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Decision Support — white, just before Final Close */}
      <section
        data-testid="decision-section"
        className="relative py-20 lg:py-24"
        style={{ background: "#ffffff" }}
      >
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div
            className="rounded-[32px] p-8 lg:p-12"
            style={{ background: "var(--cream)" }}
            data-testid="decision-support"
          >
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
              <div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-[44px] leading-tight">
                  Not sure where to start?{" "}
                  <span className="font-display-italic">
                    We’ll help you pick the right fit.
                  </span>
                </h3>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href={BOOK_CALL_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="decision-book-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all hover:scale-[1.03]"
                    style={{ background: "var(--gold)", color: "var(--ink)" }}
                  >
                    Book a Strategy Call
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="decision-whatsapp-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.03]"
                    style={{ background: "var(--whatsapp)" }}
                  >
                    <MessageCircle size={16} strokeWidth={2.5} />
                    Chat on WhatsApp
                  </a>
                </div>
              </div>

              <div className="space-y-3">
                {decisionSupport.map((d, i) => (
                  <div
                    key={d.tag}
                    className="flex items-start gap-4 rounded-2xl p-5 bg-white card-hover"
                    style={{ border: "1px solid var(--line)" }}
                    data-testid={`decision-row-${i}`}
                  >
                    <span
                      className="font-display-italic text-2xl shrink-0 mt-0.5"
                      style={{ color: "var(--gold-dark)" }}
                    >
                      0{i + 1}
                    </span>
                    <div>
                      <div className="font-display text-xl text-[var(--ink)] leading-tight">
                        Start with {d.tag}
                      </div>
                      <div
                        className="text-sm mt-0.5"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {d.hint}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Close */}
      <section
        data-testid="final-close"
        className="relative py-24 lg:py-32 overflow-hidden"
        style={{ background: "var(--ink)" }}
      >
        <div className="grain absolute inset-0" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 30%, rgba(212,164,76,0.12), transparent 70%)",
          }}
        />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2
            className="font-display text-white text-4xl sm:text-5xl lg:text-[64px] leading-[1.08]"
            data-testid="final-headline"
          >
            Right now, your WhatsApp is{" "}
            <span className="font-display-italic">
              either generating revenue
            </span>{" "}
            — or silently wasting opportunities.
          </h2>
          <p
            className="mt-6 text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed"
            style={{ color: "rgba(248,241,231,0.72)" }}
          >
            There is no middle ground. You don’t need more leads. You need a
            system that converts the ones you already have.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="final-book-btn"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm sm:text-base font-semibold transition-all hover:scale-[1.03]"
              style={{ background: "var(--gold)", color: "var(--ink)" }}
            >
              Book your Strategy Call now
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="final-whatsapp-btn"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm sm:text-base font-semibold text-white transition-all hover:scale-[1.03]"
              style={{ background: "var(--whatsapp)" }}
            >
              <MessageCircle size={18} strokeWidth={2.5} />
              Chat on WhatsApp
            </a>
          </div>

          <p
            className="mt-16 font-display-italic text-xl lg:text-2xl max-w-3xl mx-auto leading-snug"
            style={{ color: "rgba(248,241,231,0.85)" }}
            data-testid="final-positioning"
          >
            We don’t help you use WhatsApp. We turn it into a Revenue
            Multiplier for your business.
          </p>
        </div>
      </section>
    </>
  );
}
